import type { Language } from '@eformulieren/engine'
import { type AdminQueryOutcome, type Bilingual, formatDateTime } from '@eformulieren/renderer'

/**
 * Wat de pagina bij een opening door functioneel beheer zelf beslist (R-155f, R-66b, R-143, R-144,
 * ontwerp 4.8 en 9.2, opdracht 6).
 *
 * **Wanneer de vraag komt.** Een beheerder zonder toekenning krijgt van `GET /projectie` een 404 met
 * de code `beheer-kan-openen`, en alleen hij: voor ieder ander blijft het het 404 van "niet
 * gevonden" (9.2, regel 1). De pagina kan de beheerrol niet zelf zien, want het token zit in een
 * `HttpOnly`-cookie; ze leest het uit dit antwoord.
 *
 * **Wat een antwoord op de opening betekent.** 201 is geopend, en dan laadt de pagina het formulier
 * opnieuw. Een weigering wordt een zin in de taal van de lezer, zonder code en zonder JSON; de ruwe
 * keten gaat naar de console (ontwerp 11.4).
 */

function codeFrom(body: string): { readonly code?: string; readonly tot?: string } {
  try {
    const read = JSON.parse(body) as { code?: unknown; tot?: unknown } | null
    return {
      ...(typeof read?.code === 'string' ? { code: read.code } : {}),
      ...(typeof read?.tot === 'string' ? { tot: read.tot } : {}),
    }
  } catch {
    return {}
  }
}

/** Komt de vraag om als beheerder te kijken? Alleen bij 404 met `beheer-kan-openen`. */
export function isAdminRequest(status: number, body: string): boolean {
  return status === 404 && codeFrom(body).code === 'beheer-kan-openen'
}

/** Een moment als dag, maand en tijd, in de taal van de lezer en de tijdzone van de browser. */
function formatTimestamp(moment: string, language: Language): string {
  return formatDateTime(moment, language, {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const ALREADY_RUNNING: Bilingual = {
  nl: 'Er loopt al een opening door u op dit formulier, tot {tot}. Ververs de pagina om het te bekijken.',
  en: 'An opening by you on this form is already running, until {tot}. Refresh the page to view it.',
}

const NOTICES: Readonly<Record<string, Bilingual>> = {
  'heeft-al-toegang': {
    nl: 'U hebt al toegang tot dit formulier. Ververs de pagina.',
    en: 'You already have access to this form. Refresh the page.',
  },
  'geen-beheerder': {
    nl: 'U kunt dit formulier niet als beheerder openen: uw account hoort niet bij functioneel beheer.',
    en: 'You cannot open this form as an administrator: your account is not part of functional administration.',
  },
  'geen-geldig-token': {
    nl: 'Uw sessie is verlopen. Log opnieuw in en probeer het daarna nog eens.',
    en: 'Your session has expired. Sign in again and then try once more.',
  },
  'niet-gevonden': {
    nl: 'Dit formulier is er niet (meer). Ververs de pagina.',
    en: 'This form does not exist (anymore). Refresh the page.',
  },
  'ongeldig-verzoek': {
    nl: 'Het openen is niet gelukt: controleer de categorie, de toelichting en het meldingsnummer.',
    en: 'Opening did not work: check the category, the explanation and the incident number.',
  },
}

const GENERAL: Bilingual = {
  nl: 'Het openen is niet gelukt. Probeer het opnieuw.',
  en: 'Opening did not work. Please try again.',
}

/** Het antwoord van `POST /beheer/openingen` als uitkomst voor de vraag. */
export function outcomeFromOpening(status: number, body: string, language: Language): AdminQueryOutcome {
  if (status === 201) return { soort: 'geopend' }
  const { code, tot: until } = codeFrom(body)
  if (code === 'opening-loopt-al' && until !== undefined) {
    return { soort: 'geweigerd', melding: ALREADY_RUNNING[language].replace('{tot}', formatTimestamp(until, language)) }
  }
  const notice = code === undefined ? undefined : NOTICES[code]
  return { soort: 'geweigerd', melding: (notice ?? GENERAL)[language] }
}

const BAR: Bilingual = {
  nl: 'U kijkt als beheerder, alleen lezen, tot {tot}.',
  en: 'You are viewing as an administrator, read only, until {tot}.',
}

/** De tekst van de balk boven een formulier dat alleen langs een opening door beheer openstaat. */
export function noticeAdminBar(until: string, language: Language): string {
  return BAR[language].replace('{tot}', formatTimestamp(until, language))
}
